import { Button, Stack, Text } from "basis";
import { postAccountLink } from "library/api";
import { PageTracking } from "library/hoc/PageTracking";
import { useEffect, useState } from "react";
import { Layout } from "../../Layout/Layout";
import { MaskedInput } from "../../MaskedInput/MaskedInput";
import { SvgInline } from "../../SvgInline/SvgInline";
import { LinkErrorMessage } from "./LinkErrorMessage";
import { getFatalErrorContent } from "../LinkingErrorContent";

export const CreditCardComponent = ({ setScreen, params }) => {
  const [state, setState] = useState({
    cardNumber: "",
    dob: "",
    pageError: false,
    errorMsg: "",
    errorEnum: "",
    fieldError: false,
    loading: false,
    fatalError: false,
  });
  const {
    cardNumber,
    dob,
    pageError,
    errorEnum,
    fieldError,
    loading,
    fatalError,
  } = state;
  const { currentStep, backClick } = params;

  const updateState = (newState) =>
    setState({
      ...state,
      ...newState,
    });
  const onNextClick = async (event) => {
    event.preventDefault();

    updateState({ loading: true });

    const number = cardNumber.replace(/\s/g, "");
    const dateOfBirth = dob.split(" / ").reverse().join("-");
    const newState = {
      loading: false,
    };

    try {
      const { data } = await postAccountLink({
        data: {
          type: "CREDIT_CARD",
          credit_card: {
            number,
            date_of_birth: dateOfBirth,
            types: ["CREDIT_CARD", "ACCOUNT_NUMBER"],
          },
        },
      });
      return setScreen("OTP", {
        ...params,
        productId: data.link.id,
        siteSubSection: "link-product-cards",
        currentStep: currentStep + 1,
        linkId: data.link.id,
        account_last4: data.link.credit_card.account_last4,
        maskedMobile: data.link.masked_mobile,
      });
    } catch (e) {
      if (e.response && e.response.data) {
        newState.errorEnum = e.response.data.type;
        newState.errorMsg = e.response.data.message;
        newState.fatalError = getFatalErrorContent(e.response.data);
      }

      newState.pageError = true;
      newState.fieldError = true;
    }

    updateState(newState);

    return null;
  };

  useEffect(() => {
    if (fatalError) {
      setScreen("FatalError", {
        ...params,
        error: fatalError,
      });
    }
  }, [fatalError, params, setScreen]);

  return (
    <Layout>
      <Layout.Header>Link credit card account</Layout.Header>
      <Layout.Main>
        <form>
          <Stack gap="6" width="360">
            <Text as="h3" textStyle="heading5">
              Account details
            </Text>
            <MaskedInput
              label="Credit card number or Account number"
              placeholder="XXXX XXXX XXXX XXXX"
              mask="9999 9999 9999 9999"
              value={cardNumber}
              onChange={(e) =>
                updateState({
                  fieldError: false,
                  cardNumber: e.target.value,
                })
              }
              error={fieldError}
            />
            <MaskedInput
              maskChar=" "
              label="Date of birth"
              placeholder="DD / MM / YYYY"
              mask="99 / 99 / 9999"
              value={dob}
              onChange={(e) =>
                updateState({
                  fieldError: false,
                  dob: e.target.value,
                })
              }
              error={fieldError}
            />
            {pageError && <LinkErrorMessage errorEnum={errorEnum} />}
            <Stack gap="6" direction-xs="horizontal">
              <Button
                type="submit"
                width="168"
                loading={loading}
                disabled={fieldError || !dob || !cardNumber}
                onClick={onNextClick}
              >
                Next
              </Button>
              <Button
                variant="secondary"
                width="168"
                onClick={() =>
                  backClick ? setScreen(backClick) : setScreen("AccountType")
                }
              >
                Back
              </Button>
            </Stack>
          </Stack>
        </form>
      </Layout.Main>
      <Layout.Sidebar>
        <Stack gap="6">
          <Text as="h4" textStyle="heading5">
            Where is my credit card number?
          </Text>
          <Text>
            You can find your 16 digit number on the front of your credit card.
          </Text>
          <SvgInline name="credit-card-number" size="192" />
        </Stack>
      </Layout.Sidebar>
    </Layout>
  );
};

const pageData = {
  page: {
    pageType: "input-page",
    siteSection: "accounts",
    siteSubSection: "link-product-cards",
    pageTitle: "details",
  },
};

export const CreditCard = ({ Component = CreditCardComponent, ...props }) => (
  <PageTracking pageData={pageData}>
    <Component {...props} />
  </PageTracking>
);
